import { useMemo, useState, type KeyboardEvent } from 'react';
import { TextField } from '@scripsteam/scrips-signal-ds';

/**
 * The "type to search, then add" field of Account settings (Language spoken, Specialities, Procedural skills), drawn as
 * the approved design's PaSetTagPicker: the text field, the results as a list right under it while open (the label and
 * a secondary code per row, the active row on the panel grey; "Searching…" / "No matches"), then the muted hint.
 *
 * Local stand-in (to migrate to Signal DS): the DS has no inline result list under a TextField; the PA used its
 * `SearchPicker` popover, which opens a SECOND search input of its own. This draws the design's single field instead.
 * Pure: the host owns the text, the open state, the search hook and what a pick does (unchanged handlers).
 * Rows are filtered by the typed text on label or code, as the SearchPicker did, so a late response for an older term
 * never shows rows that do not match. Keyboard: ArrowDown / ArrowUp move the active row, Enter picks it, Escape closes. A pointer pick keeps focus in the field.
 */
export interface TagSearchOption {
  id: string;
  label: string;
  secondary?: string;
}

export interface TagSearchFieldProps {
  value: string;
  placeholder: string;
  hint?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSearchChange: (next: string) => void;
  loading?: boolean;
  options: TagSearchOption[];
  onSelect: (id: string) => void;
}

export function TagSearchField({
  value,
  placeholder,
  hint,
  open,
  onOpenChange,
  onSearchChange,
  loading,
  options: allOptions,
  onSelect,
}: TagSearchFieldProps) {
  const [active, setActive] = useState(0);
  const options = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.label.toLowerCase().includes(q) || (o.secondary ?? '').toLowerCase().includes(q));
  }, [allOptions, value]);
  const showMenu = open && (value.trim().length > 0 || !!loading);
  const activeIndex = Math.min(active, Math.max(options.length - 1, 0));

  const pick = (id: string) => {
    onSelect(id);
    setActive(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onOpenChange(false);
      return;
    }
    if (!showMenu || loading || options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(options[activeIndex].id);
    }
  };

  return (
    <div data-slot="tag-search-field" className="relative">
      <TextField
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onFocus={() => onOpenChange(true)}
        onBlur={() => onOpenChange(false)}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          onSearchChange(e.target.value);
          setActive(0);
          onOpenChange(true);
        }}
      />
      {showMenu && (
        <ul data-slot="tag-search-menu" role="listbox" aria-label={placeholder}>
          {loading && options.length === 0 ? (
            <li data-slot="tag-search-message">Searching…</li>
          ) : options.length === 0 ? (
            <li data-slot="tag-search-message">No matches</li>
          ) : (
            options.map((o, i) => (
              <li
                key={o.id}
                role="option"
                aria-selected={i === activeIndex}
                data-slot="tag-search-option"
                data-active={i === activeIndex ? '' : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o.id)}
              >
                <span>{o.label}</span>
                {o.secondary && <span data-slot="tag-search-code">{o.secondary}</span>}
              </li>
            ))
          )}
        </ul>
      )}
      {hint && <p data-slot="tag-search-hint">{hint}</p>}
    </div>
  );
}
